import "./BotChat.scss";
import { motion } from "framer-motion";
import { useState } from "react";
import { ScaleLoader } from "react-spinners";
import newRequest from "../../utils/newRequest";
import { useMemo } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { getTimeAgo, logData } from "../../utils/genericFunctions";
import { useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import InitialsAvatar from "../../components/InitialsAvatar/InitialsAvatar";
import { useSelector } from "react-redux";
import { FiAtSign, FiPaperclip, FiSearch } from "react-icons/fi";
import { MdArrowBackIos, MdClose, MdEdit, MdOutlineCancel } from "react-icons/md";
import { FaHandsHelping } from "react-icons/fa";
import { GiOverInfinity } from "react-icons/gi";
import CircularButton, { CustomLoadingButton } from "../../components/ui/CustomButton";
import { v4 as uuidv4 } from "uuid";
import { IoCheckmarkDoneSharp, IoSend } from "react-icons/io5";
import { HiOutlineDownload } from "react-icons/hi";
import { LuFileImage } from "react-icons/lu";
import { BsFileEarmarkPdfFill } from "react-icons/bs";
import { IoDocument } from "react-icons/io5";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import DOMPurify from 'dompurify';
import { useLocation, useNavigate } from "react-router-dom";

// date formatter
const fmtDateLabel = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toLocaleDateString([], { year: "numeric", month: "short", day: "2-digit" });
};

// time formatter
const fmtTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const fmtRelative = (d) => {
    if (!d) return "";
    const ms = Date.now() - new Date(d).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
};

const pillFromStatus = (status, controlMode) => {
    if (status === "completed") {
        return { text: "Resolved", tone: "gray" };
    }

    if (controlMode === "agent") {
        return { text: "Agent Handling", tone: "orange" };
    }

    if (status === "escalated") {
        return { text: "Agent Required", tone: "orange" };
    }

    if (status === "active" && controlMode === "bot") {
        return { text: "Bot Handling", tone: "green" };
    }

    return { text: String(status || "Unknown"), tone: "gray" };
};

const BotChat = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(true);
    const didInitialLoadRef = useRef(false);
    const pollRef = useRef(null);
    const lastCursorRef = useRef({ last_created_at: null, last_id: null });
    const showBack = location.state?.showBack || "N";
    const messagesScrollRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    const { currentUser } = useSelector((state) => state.user);
    const { currentTheme } = useSelector((state) => state.theme);

    const [conversationDocs, setConversationDocs] = useState([]);
    const [openDocSessions, setOpenDocSessions] = useState({});

    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");

    // attachments (composer): { tmp_id, name, mime, size_bytes, file }
    const [attachments, setAttachments] = useState([]);
    const textareaRef = useRef(null);

    const isNearBottom = (el, thresholdPx = 120) => {
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight < thresholdPx;
    };

    // right panel
    const [tab, setTab] = useState("active"); // active | completed
    const [q, setQ] = useState("");
    const [clients, setClients] = useState([]);
    const [selectedPhone, setSelectedPhone] = useState(null);

    // conversation
    const [thread, setThread] = useState(null); // { sessions, latestSession, items }
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState("");

    const [showVehicleEditor, setShowVehicleEditor] = useState(false);
    const [savingVehicle, setSavingVehicle] = useState(false);
    const [vehicleForm, setVehicleForm] = useState({
        reg_no: "",
        chassis_no: "",
        engine_no: "",
        color: "",
        make: "",
        model: "",
        body_type: "",
        year_of_manufacture: "",
        cc: "",
        tare_weight: "",
        passengers: "",
        sum_insured: "",
    });


    // composer
    const [text, setText] = useState("");
    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionIndex, setMentionIndex] = useState(0);


    const msgEndRef = useRef(null);

    // sending + upload progress 
    const [uploading, setUploading] = useState(false);
    const [uploadCurrentName, setUploadCurrentName] = useState("");
    const [uploadProgress, setUploadProgress] = useState(0); // current file
    const [uploadOverall, setUploadOverall] = useState(0); // overall

    const selectedClient = useMemo(() => {
        if (!selectedPhone) return null;
        return clients.find((c) => String(c.channel_user_id) === String(selectedPhone)) || null;
    }, [clients, selectedPhone]);

    const latestSessionId = thread?.latestSession?._id || selectedClient?.latest_session_id;
    const latestSession = thread?.latestSession || null;
    const latestControlMode = thread?.latestSession?.control?.mode || "bot";
    const isBotMode = latestControlMode === "bot";
    const isAgentMode = latestControlMode === "agent";
    const isCompleted = latestSession?.status === "completed";
    const assignedAgentId = thread?.latestSession?.control?.agent_id || null;
    const isMine =
        !assignedAgentId || String(assignedAgentId) === String(currentUser?._id || currentUser?.usr_docid || "");

    const refreshCurrentView = async ({ silent = true } = {}) => {
        if (!selectedPhone) return;
        await Promise.all([
            loadThread(selectedPhone),
            loadDocuments(selectedPhone),
            loadClients({ silent }),
        ]);
    };

    const loadClients = async ({ silent = false } = {}) => {
        if (!silent && !didInitialLoadRef.current) setIsLoading(true);

        const qs = new URLSearchParams({
            limit: "100",
            skip: "0",
            channel: "whatsapp",
            ...(q.trim() ? { q: q.trim() } : {}),
        }).toString();

        const res = await newRequest.get(`/chat/listChats?${qs}`);
        const rows = res?.data?.rows || [];

        setClients(rows);

        if (!selectedPhone && rows.length) {
            setSelectedPhone(String(rows[0].channel_user_id));
        }

        didInitialLoadRef.current = true;
        if (!silent && isLoading) setIsLoading(false);
    };

    const getLastMessageCursor = (payload) => {
        const msgItems = (payload?.items || []).filter(
            (x) => x?.type === "message" && x?._id
        );

        const last = msgItems[msgItems.length - 1];

        return last
            ? {
                last_created_at: last.created_at || null,
                last_id: last._id || null,
            }
            : {
                last_created_at: null,
                last_id: null,
            };
    };

    const mergeUniqueThreadItems = (prev, payload) => {
        if (!prev) return payload;

        const existingMessageIds = new Set(
            (prev.items || [])
                .filter((x) => x.type === "message" && x._id)
                .map((x) => String(x._id))
        );

        const existingSeparators = new Set(
            (prev.items || [])
                .filter((x) => x.type === "separator")
                .map((x) => `${x.session_id}:${x.started_at || ""}`)
        );

        const newItems = (payload.items || []).filter((item) => {
            if (item.type === "message") {
                return item._id && !existingMessageIds.has(String(item._id));
            }

            if (item.type === "separator") {
                const sepKey = `${item.session_id}:${item.started_at || ""}`;
                return !existingSeparators.has(sepKey);
            }

            return true;
        });

        return {
            ...prev,
            latestSession: payload.latestSession || prev.latestSession,
            items: newItems.length ? [...(prev.items || []), ...newItems] : (prev.items || []),
        };
    };

    const loadThread = async (phone_no) => {
        if (!phone_no) return;

        const res = await newRequest.get(
            `/chat/getConversations/${encodeURIComponent(phone_no)}?channel=whatsapp`
        );

        const payload = res?.data || null;
        setThread(payload);

        // IMPORTANT: sync polling cursor whenever you do a full reload
        lastCursorRef.current = getLastMessageCursor(payload);

        setTimeout(scrollToBottom, 50);
    };

    const loadDocuments = async (phone_no) => {
        if (!phone_no) return;

        const res = await newRequest.get(`/chat/getConversationDocs/${encodeURIComponent(phone_no)}`);
        const data = res?.data || {};
        setConversationDocs(data);

        const rows = data?.rows || [];
        if (rows.length > 0) {
            const firstSessionId = rows[0].session_id; // latest on top from backend
            setOpenDocSessions({ [firstSessionId]: true });
        } else {
            setOpenDocSessions({});
        }
    };

    const toggleDocSession = (sessionId) => {
        setOpenDocSessions((prev) => ({
            ...prev,
            [sessionId]: !prev[sessionId],
        }));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    useEffect(() => {
        loadClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadThread(selectedPhone).catch(console.error);
        loadDocuments(selectedPhone).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPhone]);


    const scrollToBottom = () => {
        if (msgEndRef.current) {
            msgEndRef.current.scrollIntoView({
                behavior: "auto"   // use "smooth" only for live updates
            });
        }
    };

    const onTextChange = (v) => {
        setText(v);

        // auto-resize (grow, no textarea scrollbar)
        requestAnimationFrame(() => {
            if (!textareaRef.current) return;
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 220)}px`;
        });

    };


    const onPickFiles = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setAttachments((prev) => [
            ...prev,
            ...files.map((f) => ({
                tmp_id: uuidv4(),
                name: f.name,
                mime: f.type || "application/octet-stream",
                size_bytes: f.size,
                file: f,
            })),
        ]);

        e.target.value = "";
    };

    const removeAttachment = (tmp_id) => {
        setAttachments((prev) => prev.filter((a) => a.tmp_id !== tmp_id));
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            if (!textareaRef.current) return;
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 220)}px`;
        });
    }, [attachments.length]);

    useEffect(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (!selectedPhone) return;

        const startPolling = () => {
            pollRef.current = setInterval(async () => {
                try {
                    if (document.hidden) return;

                    const cur = lastCursorRef.current;
                    const qs = new URLSearchParams({
                        channel: "whatsapp",
                        ...(cur.last_created_at ? { after: cur.last_created_at } : {}),
                        ...(cur.last_id ? { after_id: cur.last_id } : {}),
                    }).toString();

                    const res = await newRequest.get(
                        `/chat/getConversations/${encodeURIComponent(selectedPhone)}?${qs}`
                    );

                    const payload = res?.data;

                    if (!payload?.items?.length) {
                        if (payload?.latestSession) {
                            setThread((prev) =>
                                prev ? { ...prev, latestSession: payload.latestSession } : payload
                            );
                        }

                        await Promise.all([
                            loadClients({ silent: true }),
                            loadDocuments(selectedPhone),
                        ]);
                        return;
                    }

                    setThread((prev) => mergeUniqueThreadItems(prev, payload));

                    const nextCursor = getLastMessageCursor(payload);
                    lastCursorRef.current = {
                        last_created_at: nextCursor.last_created_at || cur.last_created_at,
                        last_id: nextCursor.last_id || cur.last_id,
                    };
                    await Promise.all([
                        loadClients({ silent: true }),
                        loadDocuments(selectedPhone),
                    ]);
                } catch (e) {
                    console.error("poll error", e?.message);
                }
            }, 3000);
        };

        (async () => {
            try {
                const res = await newRequest.get(
                    `/chat/getConversations/${encodeURIComponent(selectedPhone)}?channel=whatsapp`
                );
                const payload = res?.data;
                lastCursorRef.current = getLastMessageCursor(payload);
                startPolling();
            } catch (error) {
                console.log(error);
            }
        })();

        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPhone]);

    const onSearch = async (e) => {
        e?.preventDefault?.();
        await loadClients();
    };

    const onTakeOver = async () => {
        if (!latestSessionId) return;

        try {
            setSending(true);

            await newRequest.post(`/chat/takeover/${latestSessionId}`, {
                note: "Taken over from chat console",
            });

            await refreshCurrentView({ silent: true });
        } catch (error) {
            console.log(error);
        } finally {
            setSending(false);
        }
    };

    const onReturnToBot = async () => {
        if (!latestSessionId) return;

        try {
            setSending(true);

            await newRequest.post(`/chat/return-to-bot/${latestSessionId}`, {
                note: "Returned to bot from chat console",
            });

            await refreshCurrentView({ silent: true });
        } catch (error) {
            console.log(error);
        } finally {
            setSending(false);
        }
    };

    const onSend = async () => {
        if (!latestSessionId || !isAgentMode) return;

        const trimmed = text.trim();
        const hasText = !!trimmed;
        const hasFiles = attachments.length > 0;

        if (!hasText && !hasFiles) return;

        try {
            setSending(true);

            let res;

            // TEXT ONLY
            if (!hasFiles) {
                res = await newRequest.post(`/chat/sendMsg/${latestSessionId}`, {
                    text: trimmed,
                });
            } else {
                // TEXT +/OR FILES
                setUploading(true);
                setUploadProgress(0);
                setUploadOverall(0);
                setUploadCurrentName(attachments[0]?.name || "");

                const formData = new FormData();

                if (hasText) {
                    formData.append("text", trimmed);
                }

                attachments.forEach((a) => {
                    formData.append("files", a.file);
                });

                res = await newRequest.post(
                    `/chat/sendMsg/${latestSessionId}`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                        onUploadProgress: (progressEvent) => {
                            if (!progressEvent.total) return;
                            const percent = Math.round(
                                (progressEvent.loaded * 100) / progressEvent.total
                            );
                            setUploadProgress(percent);
                            setUploadOverall(percent);
                        },
                    }
                );
            }

            const savedMsg = res?.data?.data;

            setThread((prev) => {
                if (!prev || !savedMsg) return prev;

                return mergeUniqueThreadItems(prev, {
                    latestSession: prev.latestSession,
                    items: [{ type: "message", ...savedMsg }],
                });
            });

            if (savedMsg?._id) {
                lastCursorRef.current = {
                    last_created_at: savedMsg.created_at,
                    last_id: savedMsg._id,
                };
            }

            setText("");
            setAttachments([]);
            setUploadCurrentName("");
            setUploadProgress(0);
            setUploadOverall(0);
            setUploading(false);

            if (textareaRef.current) {
                textareaRef.current.style.height = "auto";
            }

            scrollToBottom("smooth");

            await Promise.all([
                loadClients({ silent: true }),
                loadDocuments(selectedPhone),
            ]);
        } catch (err) {
            console.error(err);
            setUploading(false);
        } finally {
            setSending(false);
        }
    };

    const canEditVehicle =
        !!thread?.latestSession?.draft?.motor?.logbook_artifact_id &&
        thread?.latestSession?.status !== "completed" &&
        thread?.latestSession?.control?.mode === "agent";

    const openVehicleEditor = () => {
        const motor = thread?.latestSession?.draft?.motor || {};
        const vehicle = motor?.vehicle || {};

        setVehicleForm({
            reg_no: vehicle?.reg_no || "",
            chassis_no: vehicle?.chassis_no || "",
            engine_no: vehicle?.engine_no || "",
            color: vehicle?.color || "",
            make: vehicle?.make || "",
            model: vehicle?.model || "",
            body_type: vehicle?.body_type || "",
            year_of_manufacture: vehicle?.year_of_manufacture || "",
            cc: vehicle?.cc || "",
            tare_weight: vehicle?.tare_weight || "",
            passengers: vehicle?.passengers || "",
            sum_insured: motor?.sum_insured || "",
        });

        setShowVehicleEditor(true);
    };


    const onSaveVehicleDetails = async () => {
        if (!latestSessionId) return;

        try {
            setSavingVehicle(true);

            const res = await newRequest.post(
                `/chat/updateVehicle/${latestSessionId}`,
                vehicleForm
            );

            if (!res?.data?.ok) {
                throw new Error(res?.data?.error || "Failed to update vehicle details");
            }

            setShowVehicleEditor(false);

            await Promise.all([
                loadThread(selectedPhone),
                loadDocuments(selectedPhone),
                loadClients({ silent: true }),
            ]);

        } catch (error) {
            let msg = "Unable to update vehicle details";
            if (error?.response?.data?.error) {
                msg = error.response.data.error;
            } else if (error?.message) {
                msg = error.message;
            }
            console.error("updateVehicle error:", msg);
            setNotifyDialogTitle(t('error'));
            setNotifyDialogText(msg);
            setIsNotifyDialogOpen(true);
            return;
        } finally {
            setSavingVehicle(false);
        }
    };

    const workflow = thread?.latestSession?.draft?.motor || {};
    const vehicle = workflow?.vehicle || {};
    const premium = workflow?.premium || {};

    useLayoutEffect(() => {
        if (shouldAutoScrollRef.current) {
            scrollToBottom();
        }
    }, [thread?.items?.length, selectedPhone]);

    return (
        <motion.div
            className="bot-chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.6 } }}
            exit={{ opacity: 0, transition: { duration: 0.6 } }}
        >
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader className="scaler-loader" />
                </div>
            ) : (
                <div className="bot-chat-wrapper">

                    {/* LEFT: customer profile + workflow + documents + controls */}
                    <div className="panel profile">
                        <div className="card">
                            <div className="card-title">{t('customerProfile').toUpperCase()}</div>
                            <div className="kv row">
                                <div className="k">{t('name')}</div>
                                <div className="v">{selectedClient?.ent_name || "-"}</div>
                            </div>
                            <div className="kv row">
                                <div className="k">{t('code')}</div>
                                <div className="v">{selectedClient?.ent_code || "-"}</div>
                            </div>
                            <div className="kv row">
                                <div className="k">{t('created')}</div>
                                <div className="v">{getTimeAgo(selectedClient?.ent_created_at || "")}</div>
                            </div>

                            {/* <div className="kv row">
                                <div className="k">Risk Score</div>
                                <div className="v">
                                    <span className="pill green">Low Risk</span>
                                </div>
                            </div> */}

                            <div className="sub-title">{t('linkedPolicies')}</div>
                            <div className="linked">
                                {(thread?.latestSession?.refs?.policy_docid && (
                                    <div className="linked-item">
                                        <div className="icon" />
                                        <div className="txt">
                                            <div className="t">{t('policy')}</div>
                                            <div className="s">{t('active')}</div>
                                        </div>
                                    </div>
                                )) || <div className="muted">No linked policies</div>}
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-title">
                                {t('workflowData').toUpperCase()}
                                {canEditVehicle &&
                                    <CircularButton
                                        currentTheme={currentTheme}
                                        onClick={openVehicleEditor}
                                        icon={<MdEdit />}
                                        width={"30px"}
                                        height={"30px"} />}
                            </div>

                            <div className="grid2">
                                <div className="kv">
                                    <div className="k">{t('regnNo')}</div>
                                    <div className="v">{vehicle?.reg_no || "-"}</div>
                                </div>
                                <div className="kv">
                                    <div className="k">{t('makeModel')}</div>
                                    <div className="v">
                                        {[vehicle?.make, vehicle?.model].filter(Boolean).join(" ") || "-"}
                                    </div>
                                </div>
                                <div className="kv">
                                    <div className="k">{t('year')}</div>
                                    <div className="v">{vehicle?.year_of_manufacture || "-"}</div>
                                </div>
                                <div className="kv">
                                    <div className="k">{t('pax')}</div>
                                    <div className="v">{vehicle?.passengers || "-"}</div>
                                </div>

                                <div className="kv">
                                    <div className="k">{t('cc')}</div>
                                    <div className="v">{vehicle?.cc || "-"}</div>
                                </div>
                                <div className="kv">
                                    <div className="k">{t('weight')}</div>
                                    <div className="v">{vehicle?.tare_weight || "-"}</div>
                                </div>

                                <div className="kv">
                                    <div className="k">{t('sumInsured')}</div>
                                    <div className="v">
                                        {workflow?.sum_insured ? `KES ${Number(workflow.sum_insured).toLocaleString()}` : "-"}
                                    </div>
                                </div>
                                <div className="kv">
                                    <div className="k">{t('premium')}</div>
                                    <div className="v">
                                        {premium.total ? `KES ${Number(premium.total).toLocaleString()}` : "-"}
                                    </div>
                                </div>
                                <div className="kv" style={{ overflow: "hidden" }}>
                                    <div className="k">{t('sessionStatus')}</div>
                                    <div className="v">
                                        <span className="pill gray" >{latestSession?.status || "-"}</span>
                                    </div>
                                </div>
                                <div className="kv">
                                    <div className="k">{t('paymentStatus')}</div>
                                    <div className="v">
                                        <span className="pill orange">
                                            {premium?.paid ? `Paid ${Number(premium.paid).toLocaleString()}` : "Pending"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {conversationDocs?.rows?.length > 0 && (
                            <div className="card">
                                <div className="card-title">{t('documents').toUpperCase()}</div>

                                <div className="session-docs-accordion">
                                    {conversationDocs.rows.map((row) => {
                                        const isOpen = !!openDocSessions[row.session_id];

                                        return (
                                            <>
                                                {row?.documents?.length > 0 &&
                                                    <div key={row.session_id} className="session-tree">
                                                        <button
                                                            type="button"
                                                            className={`session-title ${isOpen ? "open" : ""}`}
                                                            onClick={() => toggleDocSession(row.session_id)}
                                                        >
                                                            <span>
                                                                {new Date(row.session_date).toLocaleDateString([], {
                                                                    day: "2-digit",
                                                                    month: "long",
                                                                    year: "numeric",
                                                                })}
                                                            </span>

                                                            <span className="session-meta">
                                                                {row?.documents?.length || 0} document{(row?.documents?.length || 0) === 1 ? "" : "s"}
                                                            </span>

                                                            <span className="session-chevron">{isOpen ? "−" : "+"}</span>
                                                        </button>

                                                        {isOpen && (
                                                            <div className="docs">
                                                                {row?.documents?.map((doc) => (
                                                                    <DocRow key={doc._id} doc={doc} />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>}
                                            </>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="card">
                            <div className="card-title">{t('agentControls').toUpperCase()}</div>
                            <div className="controls">
                                {isBotMode && !isCompleted && (
                                    <CustomLoadingButton
                                        currentTheme={currentTheme}
                                        iconPosition="end"
                                        borderRadiusType="both"
                                        variant="tint"
                                        color="#43A047"
                                        width={"100%"}
                                        tooltip="Take over conversation from BOT"
                                        endIcon={<FaHandsHelping />}
                                        onClick={onTakeOver}
                                        disabled={!latestSessionId || sending}
                                    >
                                        {t('takeOver')}
                                    </CustomLoadingButton>
                                )}

                                {isAgentMode && (
                                    <CustomLoadingButton
                                        currentTheme={currentTheme}
                                        iconPosition="end"
                                        borderRadiusType="both"
                                        variant="tint"
                                        color="#3D5AFE"
                                        width={"100%"}
                                        tooltip="Return conversation to BOT"
                                        endIcon={<GiOverInfinity />}
                                        onClick={onReturnToBot}
                                        disabled={!latestSessionId || sending}
                                    >
                                        {t('returnToBot')}
                                    </CustomLoadingButton>
                                )}

                                <CustomLoadingButton
                                    currentTheme={currentTheme}
                                    iconPosition="end"
                                    borderRadiusType="both"
                                    variant="tint"
                                    color="#EC407A"
                                    width={"100%"}
                                    tooltip="Open a ticket"
                                    endIcon={<GiOverInfinity />}
                                    onClick={() => { }}
                                    disabled={!latestSessionId}
                                >
                                    {t('ticket')}
                                </CustomLoadingButton>
                            </div>
                        </div>
                    </div>

                    {/* MIDDLE: conversation */}
                    <div className="panel chat-roll">
                        <div className="chat-roll-header">
                            <div className="client-badge">
                                <InitialsAvatar
                                    name={selectedClient?.ent_name}
                                    size={40}
                                    currentTheme={currentTheme}
                                />
                                <div className="meta">
                                    <div className="name">{selectedClient?.ent_name || "Unknown Client"}</div>
                                    <div className="sub">
                                        <span>Phone: +{selectedClient?.channel_user_id || "-"}</span>
                                        <span className="dot">•</span>
                                        <span>Active since {thread?.latestSession?.started_at ? fmtTime(thread.latestSession.started_at) : "-"}</span>
                                        <span className="dot">•</span>
                                        <span>{isAgentMode ? `Agent Mode` : `Bot Mode`}</span>
                                    </div>
                                </div>
                            </div>
                            {/* <button className="ghost-btn" type="button" onClick={() => loadThread(selectedPhone)}>
                                Chat History
                            </button> */}
                            {/* This part shows if the menu was opened from home screen */}
                            {showBack === "Y" &&
                                <CircularButton currentTheme={currentTheme} onClick={() => navigate(-1)} icon={<MdClose />} width={"35px"} height={"35px"} />}
                        </div>
                        <div className="middle-search">
                            <div className="homeSearch">
                                <FiSearch className="homeSearchIcon" />
                                <input
                                    className="homeSearchInput"
                                    type="text"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search in chats (e.g. logbook)"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") onSearch();
                                    }}
                                />
                                {q?.length > 0 ? (
                                    <MdClose
                                        className="homeSearchClear"
                                        onClick={async () => {
                                            setQ("");
                                            loadClients();
                                        }}
                                    />
                                ) : (
                                    <span className="homeSearchClearPlaceholder" />
                                )}
                            </div>
                        </div>

                        <div
                            ref={messagesScrollRef}
                            className="messages-scroll"
                            onScroll={() => {
                                shouldAutoScrollRef.current = isNearBottom(messagesScrollRef.current);
                            }}
                        >
                            {thread?.items?.length ? (
                                thread.items.map((it) => {
                                    // if (it.type === "separator") {
                                    //     return (
                                    //         <div key={`sep-${it.session_id}`} className="separator">
                                    //             <span className="line" />
                                    //             <span className="label">
                                    //                 NEW CONVERSATION
                                    //                 {it.started_at ? ` • ${fmtDateLabel(it.started_at)}` : ""}
                                    //             </span>
                                    //             <span className="line" />
                                    //         </div>
                                    //     );
                                    // }

                                    const isUser = it.actor === "user" || it.direction === "inbound";
                                    const bubbleClass = isUser ? "bubble user" : it.actor === "agent" ? "bubble agent" : "bubble bot";
                                    const cleanText =
                                        it?.text && String(it.text).trim() === "." ? "" : (it?.text || "");

                                    const hasAttachments = Array.isArray(it.attachments) && it.attachments.length > 0;
                                    const isInvisiblePlaceholder =
                                        !cleanText && !hasAttachments && it?.message_type !== "media";

                                    if (isInvisiblePlaceholder) return null;

                                    return (
                                        <div key={it._id} className={`msg-row ${isUser ? "right" : "left"}`}>
                                            <div className={bubbleClass}>
                                                <div className="text">{cleanText || (it.message_type === "media" ? "[Media]" : "")}</div>

                                                {!!it.attachments?.length && (
                                                    <div className="attachments">
                                                        {it.attachments.map((a, idx) => (
                                                            <div className="attachment" key={`${it._id}-${idx}`}>
                                                                <div className="file-icon" />
                                                                <div className="file-name">{a.filename || a.kind || "attachment"}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="meta">
                                                    <>
                                                        {(it.actor || "").toUpperCase() === "USER" ?
                                                            <InitialsAvatar
                                                                name={selectedClient?.ent_name}
                                                                size={25}
                                                                currentTheme={currentTheme}
                                                            />
                                                            :
                                                            <span className="who">{(it.actor || "").toUpperCase()}</span>
                                                        }
                                                    </>
                                                    <span className="dot">•</span>
                                                    <span className="time">{fmtTime(it.created_at)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="empty">Select a client to view conversation.</div>
                            )}
                            <div ref={msgEndRef} />
                        </div>

                        {isAgentMode &&
                            <div className="chat-input">
                                <div className="composer">
                                    <textarea
                                        ref={textareaRef}
                                        value={text}
                                        onChange={(e) => onTextChange(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        rows={2}
                                        placeholder={isAgentMode ? "Type a message" : "Take over conversation to send a manual message"}
                                        disabled={!isAgentMode}
                                    />

                                    {attachments.length > 0 && (
                                        <div className="attach-row">
                                            {attachments.map((a) => (
                                                <div className="chip" key={a.tmp_id}>
                                                    <span>{a.name}</span>
                                                    <span className="x" onClick={() => removeAttachment(a.tmp_id)}>
                                                        ×
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {uploading && attachments.length > 0 && (
                                        <div className="upload-progress">
                                            <div className="up-meta">
                                                <span className="nm">
                                                    {uploading ? `Uploading: ${uploadCurrentName}` : "Uploading..."}
                                                </span>
                                                <span className="pct">{uploadOverall || uploadProgress}%</span>
                                            </div>
                                            <div className="up-bar">
                                                <div
                                                    className="fill"
                                                    style={{ width: `${uploadOverall || uploadProgress}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="composer-actions">
                                        <div className="left">
                                            <label className="icon-btn" title="Attach">
                                                <FiPaperclip />
                                                <input type="file" multiple onChange={onPickFiles} />
                                            </label>

                                            <button
                                                type="button"
                                                className="icon-btn"
                                                title="Mention"
                                                onClick={() => {
                                                    setText((p) =>
                                                        p.endsWith("@") || p.endsWith("@ ")
                                                            ? p
                                                            : `${p}${p && !p.endsWith(" ") ? " " : ""}@`
                                                    );
                                                    setShowMentions(true);
                                                    setMentionQuery("");
                                                }}
                                            >
                                                <FiAtSign />
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            className="send"
                                            onClick={onSend}
                                            disabled={!isAgentMode || sending || uploading || (!text.trim() && attachments.length === 0)}
                                            title={
                                                uploading
                                                    ? `Uploading ${uploadCurrentName} (${uploadOverall || uploadProgress}%)`
                                                    : "Send"
                                            }
                                        >
                                            <IoSend />
                                        </button>
                                    </div>
                                </div>
                            </div>}
                    </div>

                    {/* RIGHT: clients list */}
                    <div className="panel right">
                        <div className="right-header">
                            <div className="tabs">
                                <button
                                    className={`tab ${tab === "active" ? "active" : ""}`}
                                    type="button"
                                    onClick={() => setTab("active")}
                                >
                                    {t('active').toUpperCase()} ({clients.length})
                                </button>
                                <button
                                    className={`tab ${tab === "completed" ? "active" : ""}`}
                                    type="button"
                                    onClick={() => setTab("completed")}
                                >
                                    {t('completed').toUpperCase()}
                                </button>
                            </div>
                        </div>

                        <div className="client-list-scroll">
                            {clients.map((c) => {
                                const isSel = String(c.channel_user_id) === String(selectedPhone);
                                const pill = pillFromStatus(c.status, c.control_mode);
                                return (
                                    <button
                                        key={c.channel_user_id}
                                        type="button"
                                        className={`client-row ${isSel ? "selected" : ""}`}
                                        onClick={() => setSelectedPhone(String(c.channel_user_id))}
                                    >
                                        <div className="row-top">
                                            <div className="nm">{c.ent_name || "Unknown"}</div>
                                            <div className="ago">{fmtRelative(c.last_activity_at)}</div>
                                        </div>
                                        <div className="row-mid">+{c.channel_user_id}</div>
                                        <div className="row-btm">
                                            <span className="pill gray">{c.current_step || "-"}</span>
                                            <span className={`pill ${pill.tone}`}>{pill.text}</span>
                                        </div>
                                    </button>
                                );
                            })}
                            {!clients.length && <div className="empty">No conversations.</div>}
                        </div>
                    </div>
                </div>
            )}
            {showVehicleEditor && (
                <div className="overlay-dialog">
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{t('editVehicleDetails').toUpperCase()}</h1>
                            <CircularButton currentTheme={currentTheme} onClick={() => setShowVehicleEditor(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <div className="form-grid-2">
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="reg_no"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('regnNo').toUpperCase()}
                                    </label>

                                    <input
                                        id="reg_no"
                                        name="reg_no"
                                        type="text"
                                        placeholder={t('regnNo').toUpperCase()}
                                        value={vehicleForm.reg_no}
                                        className="form-input-item"
                                        style={{ width: "200px" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, reg_no: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="chassis_no"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('chassisNo').toUpperCase()}
                                    </label>

                                    <input
                                        id="chassis_no"
                                        name="chassis_no"
                                        type="text"
                                        placeholder={t('chassisNo').toUpperCase()}
                                        value={vehicleForm.chassis_no}
                                        className="form-input-item"
                                        style={{ width: "200px" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, chassis_no: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="engine_no"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('engineNo').toUpperCase()}
                                    </label>

                                    <input
                                        id="engine_no"
                                        name="engine_no"
                                        type="text"
                                        placeholder={t('engineNo').toUpperCase()}
                                        value={vehicleForm.engine_no}
                                        className="form-input-item"
                                        style={{ width: "200px" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, engine_no: e.target.value }))}
                                    />
                                </div>

                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="color"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('color').toUpperCase()}
                                    </label>

                                    <input
                                        id="color"
                                        name="color"
                                        type="text"
                                        placeholder={t('color').toUpperCase()}
                                        value={vehicleForm.color}
                                        className="form-input-item"
                                        style={{ width: "200px" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, color: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="make"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('make').toUpperCase()}
                                    </label>

                                    <input
                                        id="make"
                                        name="color"
                                        type="text"
                                        placeholder={t('make').toUpperCase()}
                                        value={vehicleForm.make}
                                        className="form-input-item"
                                        style={{ width: "200px" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, make: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="model"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('model').toUpperCase()}
                                    </label>

                                    <input
                                        id="model"
                                        name="color"
                                        type="text"
                                        placeholder={t('model').toUpperCase()}
                                        value={vehicleForm.model}
                                        className="form-input-item"
                                        style={{ width: "200px" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, model: e.target.value }))}
                                    />
                                </div>

                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="body_type"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('bodyType').toUpperCase()}
                                    </label>
                                    <input
                                        id="body_type"
                                        name="body_type"
                                        type="text"
                                        placeholder={t('bodyType').toUpperCase()}
                                        value={vehicleForm.body_type}
                                        className="form-input-item"
                                        style={{ width: "200px" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, body_type: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="yom"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('yom').toUpperCase()}
                                    </label>
                                    <input
                                        id="yom"
                                        name="yom"
                                        type="text"
                                        placeholder={t('yom').toUpperCase()}
                                        value={vehicleForm.year_of_manufacture}
                                        className="form-input-item"
                                        style={{ width: "200px", textAlign: "right" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, year_of_manufacture: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="cc"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('cc').toUpperCase()}
                                    </label>
                                    <input
                                        id="cc"
                                        name="cc"
                                        type="text"
                                        placeholder={t('cc').toUpperCase()}
                                        value={vehicleForm.cc}
                                        className="form-input-item"
                                        style={{ width: "200px", textAlign: "right" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, cc: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="weight"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('weight').toUpperCase()}
                                    </label>
                                    <input
                                        id="weight"
                                        name="weight"
                                        type="text"
                                        placeholder={t('weight').toUpperCase()}
                                        value={vehicleForm.tare_weight}
                                        className="form-input-item"
                                        style={{ width: "200px", textAlign: "right" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, tare_weight: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="pax"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('pax').toUpperCase()}
                                    </label>
                                    <input
                                        id="pax"
                                        name="pax"
                                        type="text"
                                        placeholder={t('pax').toUpperCase()}
                                        value={vehicleForm.passengers}
                                        className="form-input-item"
                                        style={{ width: "200px", textAlign: "right" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, passengers: e.target.value }))}
                                    />
                                </div>
                                <div className="input-item-with-label top">
                                    <label
                                        htmlFor="sum_insured"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('sumInsured').toUpperCase()}
                                    </label>
                                    <input
                                        id="sum_insured"
                                        name="sum_insured"
                                        type="text"
                                        placeholder="0.00"
                                        value={vehicleForm.sum_insured}
                                        className="form-input-item"
                                        style={{ width: "200px", textAlign: "right" }}
                                        onChange={(e) => setVehicleForm((p) => ({ ...p, sum_insured: e.target.value }))}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="overlay-actions">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="left"
                                variant="tint"
                                color="#0f766e5d"
                                endIcon={<MdOutlineCancel />}
                                onClick={() => setShowVehicleEditor(false)}>
                                {t('cancel').toUpperCase()}
                            </CustomLoadingButton >
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="right"
                                variant="tint"
                                color="#0f766e5d"
                                tooltip="Save changes"
                                endIcon={<IoCheckmarkDoneSharp />}
                                onClick={onSaveVehicleDetails}
                                disabled={savingVehicle}
                            >
                                {savingVehicle ? t('saving') : t('save')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </div>
            )}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notifyDialogText) }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton
                        currentTheme={currentTheme}
                        iconPosition="end"
                        endIcon={<MdClose />}
                        onClick={() => setIsNotifyDialogOpen(false)}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
        </motion.div>
    );
};

function DocRow({ doc }) {

    const handleDocumentPreview = async (docId) => {
        try {
            console.log(`docId ... ${docId}`);
            // setIsProcessing(true);
            const response = await newRequest.get(`/viewdocs/view/${docId}`, {
                responseType: 'arraybuffer',
            });
            logData("response", response);
            // Create a blob from the binary data received
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = URL.createObjectURL(blob);
            // Open the PDF in a new tab
            const newTab = window.open();
            newTab.location.href = url;
            // setIsProcessing(false);
        } catch (error) {
            console.log(error)
        }
    }

    return (
        <div className="doc-row">
            <div className="doc-left">
                {/* <div className="doc-ic" /> */}
                {["pdf"].includes(doc?.extension) && <BsFileEarmarkPdfFill className="doc-ic pdf" />}
                {["jpg", "jpeg", "png", "webp"].includes(doc?.extension) && <LuFileImage className="doc-ic img" />}
                {!["pdf", "jpg", "jpeg", "png", "webp"].includes(doc?.extension) && <IoDocument className="doc-ic doc" />}
                <div className="name-col">
                    <div className="doc-name">{`${doc?.doc_no}.${doc?.extension}`}</div>
                    <div className="doc-type">{doc?.doc_label}</div>
                    {/* <div className="doc-type">{doc?.extension}</div> */}
                </div>
            </div>
            <HiOutlineDownload className="doc-dl" onClick={() => handleDocumentPreview(doc?._id)} />
        </div>
    );
}

export default BotChat;